import contractorList, {
  ContractorsListParams
} from "@/getinfo-api/contractors";
import api from "@/getinfo-api";
import {
  getCurrentCompanyId,
  getCurrentEmployee,
  isRoot as root
} from "@/plugins/roles";
import companiesStore from "@/store/modules/companies";
import { uniqBy } from "lodash";
import { ContractorNew as IContractorNew } from "@/api/models/contractor-new";
import { User as IUser } from "@/api/models/user";
import { Company as ICompany } from "@/api/models/company";

class UserHelperClass {
  private _user: IUser | null = null;
  private _userId: string | null = null;
  private _companies: ICompany[] = [];
  private _contractors: IContractorNew[] = [];
  private _company: ICompany | null = null;
  private _isLoading: boolean = false;

  constructor(userId: string) {
    this._userId = userId;
    this.init();
  }

  get user(): IUser | null {
    return this._user;
  }

  set user(value: IUser | null) {
    this._user = value;
  }

  get userId() {
    return this._userId;
  }

  set userId(id: string | null) {
    this._userId = id;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    this._isLoading = value;
  }

  get isRoot() {
    return root();
  }

  get isEmployee() {
    return !!getCurrentEmployee();
  }

  get employeeId() {
    return getCurrentEmployee()?.id;
  }

  get employee() {
    return getCurrentEmployee();
  }

  get companies() {
    return this._companies;
  }

  set companies(e: ICompany[]) {
    this._companies = e;
  }

  get companyId() {
    return getCurrentCompanyId();
  }

  get company(): ICompany | null {
    return this._company ?? null;
  }

  set company(e) {
    this._company = e;
  }

  get currentContractor() {
    return this.contractorsBySelectedCompany?.length === 1
      ? this._contractors[0]
      : null;
  }

  get currentContractorType() {
    return this.currentContractor?.type ?? null;
  }

  get contractors(): IContractorNew[] {
    return this._contractors;
  }

  set contractors(value: IContractorNew[]) {
    this._contractors = value;
  }

  get isContractor(): boolean {
    return !!this.contractors?.length;
  }

  get isOnlyContractor(): boolean {
    return this.isContractor && !this.isRoot && !this.isEmployee;
  }

  get contractorsBySelectedCompany(): IContractorNew[] {
    return this.contractors?.filter(el => el.company?.id === this.company?.id);
  }

  get physicalContractor(): IContractorNew[] {
    return this.contractorsBySelectedCompany?.filter(
      el => el.type === "physical"
    );
  }

  get juridicalContractor(): IContractorNew[] {
    return this.contractorsBySelectedCompany?.filter(
      el => el.type === "juridical"
    );
  }

  public getResponsibleId(responsibleId?: string): string | undefined {
    if (responsibleId) return responsibleId;
    return this.currentContractor?.responsible?.id;
  }

  public getContractorId(contractorId?: string): string | undefined {
    if (contractorId) return contractorId;
    return this.currentContractor?.id;
  }

  public getContractorType(
    type?: "juridical" | "physical"
  ): string | undefined {
    if (type) return type;
    return this.currentContractor?.type;
  }

  public getContractorByType(type: "juridical" | "physical"): IContractorNew[] {
    return this.contractorsBySelectedCompany?.filter(el => el.type === type);
  }

  public async acceptConsent() {
    try {
      this.isLoading = true;
      const { data } = await api.UsersApi.consentUserToDataProcessing(
        this.userId!,
        { consent: true }
      );
      this.user = data;
      return data;
    } finally {
      this.isLoading = false;
    }
  }

  async getCurrentUser() {
    try {
      this.isLoading = true;
      const { data } = await api.UsersApi.get(this.userId!);
      this.user = data;
      return data;
    } finally {
      this.isLoading = false;
    }
  }

  async getContractorByUserId() {
    try {
      const params: ContractorsListParams = {
        userIds: [this.userId!],
        limit: 900
      };
      const { data } = await contractorList(params);
      const companies = data.items
        ?.map(el => el.company)
        ?.filter(el => el.enabled);
      this.companies = uniqBy([...companies, ...this.companies], "id");
      this.contractors = data.items?.filter(el => el.company?.enabled);
      companiesStore.mutations.addCompanies(companiesStore.state, companies);
      if (this.companies?.length) {
        if (localStorage.getItem("selectedCompany")) {
          const selected = this.companies.find((element: any) => {
            return element.id === localStorage.selectedCompany;
          });
          companiesStore.state.selectedCompany =
            selected ?? companiesStore.state.selectedCompany ?? companies[0];
        } else {
          companiesStore.state.selectedCompany =
            companies[0] ?? companiesStore.state.selectedCompany;
        }
      }
    } catch (e) {
      throw new Error(e);
    }
  }

  public destroy() {
    this.companies = [];
    this.company = null;
    this.userId = null;
    this.user = null;
    this.contractors = [];
  }

  public async init() {
    this.getCurrentUser();
    if (!this.isRoot && this.userId) {
      await this.getContractorByUserId();
    }
  }
}

export { UserHelperClass };
